import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Box, Menu, MenuItem, ListItemIcon,
  Container, CircularProgress, Divider,
} from '@mui/material';
import { ArrowBack, AccountCircle, Logout, Settings, Home } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { authService } from '../services/auth';
import SiteFooter from './SiteFooter';

export const PageBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
}));

interface LayoutProps {
  title: string;
  backTo?: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  actions?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ title, backTo, children, maxWidth = 'lg', loading, actions }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const email = authService.getEmail();

  const handleLogout = () => {
    authService.logout();
    navigate('/');
  };

  return (
    <PageBackground>
      <AppBar position="static" color="secondary">
        <Toolbar>
          {backTo && (
            <IconButton edge="start" color="inherit" onClick={() => navigate(backTo)} sx={{ mr: 1 }} aria-label="Back">
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, color: 'white' }}>
            {title}
          </Typography>
          {actions}
          <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Account menu">
            <AccountCircle />
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Typography variant="body2" color="text.secondary">{email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchor(null); navigate('/dashboard'); }}>
              <ListItemIcon><Home fontSize="small" /></ListItemIcon>My Secret Santas
            </MenuItem>
            <MenuItem onClick={() => { setAnchor(null); navigate('/settings'); }}>
              <ListItemIcon><Settings fontSize="small" /></ListItemIcon>Notification settings
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Container maxWidth={maxWidth} sx={{ mt: { xs: 2, md: 4 }, mb: 4, flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={48} color="secondary" />
          </Box>
        ) : children}
      </Container>
      <SiteFooter />
    </PageBackground>
  );
};

export default Layout;
